import React, {Fragment} from 'react'
import styles from './SaveRoute.scss'
import {connect, Provider} from 'react-redux'
import {initStore} from './redux/initStore'
import {SaveSuccess} from './SaveSuccess'
import {Route, Trip} from 'data/records'
import {routeActions, routeSelectors} from 'data/resources/routes'
import {tripActions, tripSelectors} from 'data/resources/trips'
import {clubActions, clubSelectors} from 'data/resources/clubs'
import {gearActions, gearSelectors} from 'data/resources/gears'
import {eventActions} from 'data/resources/events'
import {experienceActions, experienceSelectors} from 'data/resources/experiences'
import {experienceRoutesActions} from 'lib/api/experiences/routes'
import {photoActions, photoSelectors} from 'data/resources/photos'
import {Loadable} from 'common/Loadable'
import {Icon} from 'common/Icon'
import {CircleSpinner} from 'common/spinners'
import {SearchableMultiSelect} from 'common/SearchableMultiSelect'
import {ModalOverlay} from 'common/styleguide/ModalOverlay'
import {CTA} from 'common/styleguide/CTA'
import {CheckboxGroup} from 'common/styleguide/CheckboxGroup'
import {DatePickerInput} from 'common/styleguide/DatePickerInput'
import {ImageUpload} from 'common/styleguide/ImageUpload'
import {RadioGroup} from 'common/styleguide/RadioGroup'
import {TimePickerInput} from 'common/styleguide/TimePickerInput'
import {getCurrentUser, getPhotoUploadMaxConcurrency} from 'lib/utils'
import {urls} from 'lib/urls'
import fetchJSON from 'lib/fetchJSON'
import plusIcon from 'assets/images/goals/plusIcon.svg'
import {Map, Set, List, OrderedMap} from 'immutable'
import Select from 'react-select'
import PromisePool from 'es6-promise-pool'
import moment from 'moment'
import cx from 'classnames'

const VISIBILITY_OPTIONS = [
  {value: 0, title: 'Public'},
  {value: 1, title: 'Private'},
  {value: 2, title: 'Friends Only'}
]

const CLUB_VISIBILITY_OPTIONS = [
  {value: 0, title: 'Public'},
  {value: 1, title: 'Route managers'},
  {value: 2, title: 'Members'}
]

const RECREATION_TYPES = OrderedMap({
  cycling: 'Cycling',
  mountain: 'Mountain Biking',
  cyclocross: 'Cyclocross Biking',
  hiking: 'Hiking',
  running: 'Running',
  motorcycle: 'Riding a motorcycle'
})

// these are the RectreationType ids from prod
const RECREATION_TYPE_IDS = OrderedMap({
  cycling: 3,
  mountain: 4,
  cyclocross: 10,
  hiking: 6,
  running: 5,
  motorcycle: 1
})

const YES_NO_OPTIONS = OrderedMap({
  false: 'No',
  true: 'Yes'
})

export class _SaveRoute extends React.PureComponent {
  constructor (props) {
    super(props)

    // undefined activeRoute from props -> new route, in which case
    // props.map.activeRoute (the js from the planner) will still have values
    const {activeRoute, map: {routes, activeRoute: activeRouteFromPlanner}, loadableResources: {troute, privClubs}} = props
    const recTypeIdsFlipped = RECREATION_TYPE_IDS.flip()
    const experienceIds = Set(activeRoute ? activeRoute.experienceIds : [])
    // this is ugly, but so are groups
    const groupId = troute && troute.value.route && privClubs && !privClubs.find(c => c.userId === troute.value.route.userId)
      ? props.loadableResources.troute.value.route.groupId
      : null
    const visibility = activeRoute
      ? activeRoute.visibility
      : (props.currentUser && props.currentUser.preferences && props.currentUser.preferences.default_privacy_route) || 0

    this.state = {
      name: activeRoute ? activeRoute.name : '',
      description: activeRoute ? activeRoute.description : '',
      visibility,
      ownerId: activeRoute ? activeRoute.userId : props.currentUser.id,
      eventIds: Set(activeRoute ? activeRoute.eventIds : []),
      events: Map(), // ownerId -> eventId -> {id, name}
      experienceIds,
      initialExperienceIds: experienceIds,
      photoIds: Set(activeRoute ? activeRoute.photoIds : []),
      files: List(),
      recreationTypeIds: Set(activeRoute && activeRoute.recreationTypeIds
        ? activeRoute.recreationTypeIds.map(rid => recTypeIdsFlipped.get(rid))
        : []),
      recordAsTrip: false,
      showMoreOptions: false,
      anyEdits: props.map.routes.filter(r => !r.isTrip && r.id).length > 0,
      // NOTE: editId ends up being used as a key, so it'll be
      // coerced into a string. we go ahead and do that here to
      // avoid confusion later. this also means that for new routes,
      // editId is the string 'null'. this is also true in the case
      // that the user has clicked save on a trip imported to the planner
      editId: `${activeRouteFromPlanner.isTrip || (activeRoute && !activeRoute.permissions.canBeModified)
        ? null
        : activeRouteFromPlanner.id}`,
      // these are ids of any routes that have been loaded into the planner
      mapRouteIds: routes.map(r => !r.isTrip && r.id).filter(id => id),
      departedAtDate: null,
      departedAtTime: null,
      duration: null,
      gearId: null,
      club: window.rwgps && window.rwgps.config && window.rwgps.config.club,
      groups: props.loadableResources.groups,
      groupId,
      auditNotes: '',
      availableTags: [],
      selectedTags: activeRoute ? activeRoute.tagNames : [], // [String]
      locality: activeRoute ? activeRoute.locality : '',
      createNewPending: false,
      savePending: false,
      saveSuccess: false,
      pendingUploads: 0
    }
  }

  componentDidMount () {
    const {
      currentUser,
      map: {routes},
      loadEvents,
      loadRoute,
      loadExperiences,
      loadGears
    } = this.props
    const {club, events} = this.state
    const cuId = currentUser && currentUser.id

    // these fields are below the fold, so shouldn't block inital render
    const gearsPromise = () => loadGears()
    const tagsPromise = () => fetchJSON(`/organizations/${club.id}/available_tags.json`)
      .then(r => {
        this.setState({availableTags: r.route_tags})
      })
    const routesPromises = () => routes
      .map(r => !r.isTrip && r.id)
      .filter(id => id)
      .map(rid => loadRoute(rid).then(resp => resp.value.route))
    const eventsPromise = () => loadEvents(cuId).then(r => {
      const nextEvents = events.withMutations(m => r.events.forEach(e => m.setIn([cuId, e.id], e)))
      this.setState({events: nextEvents})
    })
    const experiencePromise = () => loadExperiences(club.id)

    Promise.all([
      ...routesPromises(),
      cuId && eventsPromise(),
      club && experiencePromise(),
      club && tagsPromise(),
      !club && gearsPromise()
    ]).then(() => this.setState({resourceLoaded: true}))
  }

  onTitleChange = (e) => {
    this.setState({name: e.target.value})
  }

  onDescriptionChange = (e) => {
    this.setState({description: e.target.value})
  }

  onVisibilityChange = (e) => {
    this.setState({visibility: e.target.value})
  }

  onOwnerIdChange = (ownerId) => {
    const {clubByUserId, loadEvents, loadExperiences} = this.props
    const {events, recordAsTrip} = this.state
    const club = clubByUserId(ownerId)
    const clubId = club && club.id

    if (this.state.ownerId !== ownerId) {
      const eventsPromise = () => loadEvents(ownerId).then(r => {
        const nextEvents = events.withMutations(m => r.events.forEach(e => m.setIn([ownerId, e.id], e)))
        this.setState({events: nextEvents, eventIds: Set([])})
      })
      Promise.all([eventsPromise(), clubId && loadExperiences(clubId)])
        .then(_ => {
          // don't save club routes as trips
          const nextRecordAsTrip = clubId ? false : recordAsTrip

          this.setState({ownerId, recordAsTrip: nextRecordAsTrip, groupId: null})
        })
    }
  }

  onGroupIdChange = (groupId) => {
    this.setState({groupId, ownerId: this.props.currentUser.id})
  }

  onEventIdsChange = (id) => {
    const {eventIds, recordAsTrip} = this.state
    const nextEventIds = eventIds.has(id) ? eventIds.delete(id) : eventIds.add(id)
    // event routes shouldn't be saved as trips
    const nextRecordAsTrip = nextEventIds.size > 0 ? false : recordAsTrip

    this.setState({eventIds: nextEventIds, recordAsTrip: nextRecordAsTrip})
  }

  onExperienceIdsChange = (id) => {
    const {experienceIds} = this.state
    const nextExperienceIds = experienceIds.has(id) ? experienceIds.delete(id) : experienceIds.add(id)

    this.setState({experienceIds: nextExperienceIds})
  }

  onPhotosDrop = (files) => {
    if (!files[0]) { return }
    this.setState({files: this.state.files.concat(files)})
  }

  onPhotoRemoved = (pid) => {
    this.setState({photoIds: this.state.photoIds.delete(pid)})
  }

  onFileRemoved = (e, idx) => {
    e.preventDefault()
    const {files} = this.state

    this.setState({files: files.delete(idx)})
  }

  onRecreationTypeIdsChange = (e) => {
    this.setState({recreationTypeIds: e})
  }

  onRecordAsTripChange = (recordAsTrip) => {
    this.setState({recordAsTrip: recordAsTrip === 'true'})
  }

  onEditIdChange = (e) => {
    this.setState({editId: e})
  }

  onDepartedAtChange = (v, field) => {
    // datepicker stores as Date, timepicker stores as moment :/
    const nextDepartedAt = moment.isMoment(v) ? v.toDate() : v
    this.setState({[field]: nextDepartedAt})
  }

  onDepartedAtDateChange = (v) => this.onDepartedAtChange(v, 'departedAtDate')

  onDepartedAtTimeChange = (v) => this.onDepartedAtChange(v, 'departedAtTime')

  constructDepartedAt = (date, time) => {
    if (!date && !time) {
      return null // otherwise we run the risk of departingAt december of 1899
    }

    const year = date && date.getFullYear()
    const month = date && date.getMonth()
    const day = date && date.getDate()
    const hour = time && time.getHours()
    const minute = time && time.getMinutes()

    return new Date(year, month, day, hour, minute).toISOString()
  }

  onDurationChange = (e) => {
    this.setState({duration: parseInt(e.target.value, 10)})
  }

  onGearIdChange = (e) => {
    this.setState({gearId: e.target.value})
  }

  onAuditNotesChange = (e) => {
    this.setState({auditNotes: e.target.value})
  }

  onSelectedTagsChange = (values) => {
    const selectedTags = values.map(v => v.value)
    this.setState({selectedTags})
  }

  onLocalityChange = (e) => {
    this.setState({locality: e.target.value})
  }

  objectType = recordAsTrip => recordAsTrip ? 'trip' : 'route'

  onSubmit = (e) => {
    e.preventDefault()
    this.setState({savePending: true})

    const {
      map: {plannerOptions},
      createTRoute,
      updateRoute,
      addPhoto,
      addExperienceRoute,
      removeRouteFromExperience,
      ps: pointsOfInterest,
      clearPlannerSaved
    } = this.props
    const {track_points: rawPoints, course_points: coursePoints} = this.props.map.activeRoute
    const {
      name,
      description,
      visibility,
      ownerId,
      editId,
      eventIds,
      experienceIds,
      initialExperienceIds,
      photoIds,
      files,
      recreationTypeIds,
      recordAsTrip,
      departedAtDate,
      departedAtTime,
      duration,
      auditNotes,
      selectedTags,
      locality,
      gearId,
      club,
      groupId
    } = this.state
    const departedAt = this.constructDepartedAt(departedAtDate, departedAtTime)

    const record = (recordAsTrip) => {
      if (recordAsTrip) {
        return Trip({
          name,
          description,
          visibility,
          userId: ownerId,
          departedAt,
          duration: duration * 60, // duration is stored in minutes in the form, seconds on the record
          gearId,
          rawPoints,
          pointsOfInterest
        })
      } else {
        return Route(Object.assign({
          name,
          description,
          visibility,
          userId: ownerId,
          eventIds,
          recreationTypeIds: !recreationTypeIds.isEmpty() && recreationTypeIds.map(rt => RECREATION_TYPE_IDS.get(rt)),
          rawPoints,
          coursePoints,
          pointsOfInterest,
          plannerOptions
        },
        editId && { // don't attempt to parse id if it's 'null'
          id: parseInt(editId, 10)
        },
        club && {
          tagNames: selectedTags,
          locality
        },
        groupId && {
          groupId
        }))
      }
    }

    const type = this.objectType(recordAsTrip)
    let method, needsPhotoAssociation, params
    if (editId === 'null') {
      method = createTRoute
      needsPhotoAssociation = photoIds.size > 0
      params = record(recordAsTrip)
    } else {
      method = updateRoute
      needsPhotoAssociation = false
      params = auditNotes ? {[type]: record(recordAsTrip), auditNotes} : record(recordAsTrip)
    }

    method(params, type)
      .then(response => {
        // clears planner localstorage.
        clearPlannerSaved()

        // PhotosController#create wants parent_type and parent_id, so we have
        // to wait for our troute to be created before we can add photos to it
        if (files.size > 0) {
          this.setState((prevState) => ({pendingUploads: prevState.pendingUploads + files.size}))
          let index = 0

          const nextUpload = () => {
            if (!files.get(index)) { return null }
            const nextUpload = files.get(index)
            index++

            return addPhoto(nextUpload, type, response.value[type].id)
              .then((r) => this.setState((prevState) => {
                const nextPendingUploads = prevState.pendingUploads - 1
                const savePending = nextPendingUploads !== 0
                const saveSuccess = nextPendingUploads === 0
                return {pendingUploads: nextPendingUploads, savePending, saveSuccess}
              }))
          }
          const pool = new PromisePool(nextUpload, getPhotoUploadMaxConcurrency())
          pool.start()
        }

        if (needsPhotoAssociation) {
          photoIds.valueSeq().forEach(pid => fetchJSON('/photosets/associate_photo.json',
            {
              photo_id: pid,
              parent_id: response.value[type].id,
              parent_type: type
            }
          ))
        }

        const troute = response.value[type]

        // create experienceRoutes for new experienceIds
        experienceIds.subtract(initialExperienceIds).valueSeq().forEach(eid => addExperienceRoute(eid, troute.id))
        // remove experienceRoutes for any experienceIds we've removed
        initialExperienceIds.subtract(experienceIds).valueSeq().forEach(eid => removeRouteFromExperience(eid, troute.id))

        this.setState((prevState) => {
          // if we have enough pendingUploads to hit our max photo upload
          // concurrency, let's go ahead and let the resolution of the
          // PromisePool mark the saveSuccess
          const done = prevState.pendingUploads < getPhotoUploadMaxConcurrency()
          return Object.assign({troute}, done && {
            savePending: false,
            saveSuccess: true
          })
        })
      })
  }

  createNewEvent = (name) => {
    const {events, eventIds, ownerId} = this.state
    this.setState({createNewPending: true}, () => {
      this.props.createEvent(name, ownerId)
        .then(r => {
          const {name, id} = r.value.event
          const nextEvents = events.setIn([ownerId, id], {name, id})
          return this.setState({eventIds: eventIds.add(id), events: nextEvents, createNewPending: false})
        })
        .catch(this.setState({createNewPending: false}))
    })
  }

  createNewExperience = (name) => {
    const {club, ownerId, experienceIds} = this.state
    const {loadableResources: {privClubs}, createExperience} = this.props
    const clubId = club ? club.id : privClubs.find(c => c.userId === ownerId).id
    this.setState({createNewPending: true}, () => {
      createExperience(name, clubId)
        .then(r => this.setState({experienceIds: experienceIds.add(r.value.experience.id), createNewPending: false}))
        .catch(this.setState({createNewPending: false}))
    })
  }

  onRequestClose = (e) => {
    e.preventDefault()
    const {cleanUpModal, onRequestClose} = this.props
    const {troute, recordAsTrip} = this.state
    const saved = troute !== undefined
    let serialized, route, trip
    if (saved) {
      serialized = recordAsTrip
        ? Trip.serialize(troute)
        : Route.serialize(troute)
      route = serialized.route && Object.assign(serialized.route, {id: troute.id})
      trip = serialized.trip && Object.assign(serialized.trip, {id: troute.id})
    }

    // reopens side and bottom panels of the planner.
    // syncs response to planner sidebar.
    // function passed from rails js
    cleanUpModal({saved, route, trip})
    onRequestClose()
  }

  showMoreOptionsClicked = () => {
    this.setState({showMoreOptions: true}, () => {
      this._moreOptions.scrollIntoView({behavior: 'smooth'})
    })
  }

  render () {
    const {
      name,
      description,
      visibility,
      ownerId,
      events,
      eventIds,
      experienceIds,
      showMoreOptions,
      anyEdits,
      editId,
      photoIds,
      files,
      recreationTypeIds,
      recordAsTrip,
      mapRouteIds,
      departedAtDate,
      departedAtTime,
      duration,
      gearId,
      club,
      groups,
      groupId,
      auditNotes,
      availableTags,
      selectedTags,
      locality,
      resourceLoaded,
      createNewPending,
      savePending,
      saveSuccess,
      troute
    } = this.state
    const {experiences, gears, clubByUserId, routes, currentUser, loadableResources: {event: loadableEvent, privClubs}} = this.props
    const willHaveEvents = loadableEvent.length > 0
    const eventsForOwner = events.get(ownerId, Map())
    const selectedClub = clubByUserId(ownerId) || club
    const experiencesForOwner = selectedClub && experiences(selectedClub.id)

    if (saveSuccess) {
      const organizedInEvents = eventsForOwner
        .filter(e => eventIds.has(e.id))
      const organizedInExperiences = experiencesForOwner && experiencesForOwner
        .filter(e => experienceIds.has(e.id))
        .map(e => ({id: e.id, name: e.name}))
      // can only save to one club at a time
      const organizedInClub = privClubs && privClubs.find(c => (c.userId === ownerId && c.userId !== currentUser.id))

      return <SaveSuccess
        troute={troute}
        organizedInEvents={organizedInEvents}
        organizedInExperiences={organizedInExperiences}
        organizedInClub={organizedInClub}
        objectType={this.objectType(recordAsTrip)}
        onRequestClose={this.onRequestClose} />
    }

    return (
      <ModalOverlay
        maxWidth='600px'
        title='Save Route'
        shouldCloseOnOverlayClick={false}
        onRequestClose={this.onRequestClose}
        footer={<Footer onSubmit={this.onSubmit} savePending={savePending} onRequestClose={this.onRequestClose} />}>
        <form onSubmit={this.onSubmit} className={styles.SaveRouteForm}>

          {anyEdits && <UpdatePicker editId={editId} routes={routes} mapRouteIds={mapRouteIds} onChange={this.onEditIdChange} />}

          <label>Title</label>
          <input
            type='text'
            onChange={this.onTitleChange}
            defaultValue={name}
            placeholder='Unnamed route' />

          <label>Description</label>
          <textarea
            onChange={this.onDescriptionChange}
            defaultValue={description} />

          <label>Visibility</label>
          <select className={styles.overwriteLegacySelect} defaultValue={visibility || 0} onChange={this.onVisibilityChange}>
            {(club
              ? CLUB_VISIBILITY_OPTIONS
              : VISIBILITY_OPTIONS).map(o => <option key={o.value} value={o.value}>{o.title}</option>)}
          </select>

          {!recordAsTrip && !club && (privClubs.length > 0 || (groups && groups.length > 0)) && <Fragment>
            <label>Account</label>
            <select
              className={styles.overwriteLegacySelect}
              value={groupId || ownerId}
              onChange={(e) => {
                // do some hacky shit to support groups
                const option = [].slice.call(e.target.querySelectorAll('option')).find(c => c.value === e.target.value)
                return option.dataset.isgroup ? this.onGroupIdChange(parseInt(e.target.value, 10)) : this.onOwnerIdChange(parseInt(e.target.value, 10))
              }}>
              <option value={currentUser.id}>Personal</option>
              {privClubs.map(c => <option key={c.id} value={c.userId}>{c.name}</option>)}
              {groups.map(g => <option key={g.id} value={g.id} data-isgroup='true'>{g.name}</option>)}
            </select>
          </Fragment>}

          {experiencesForOwner && !experiencesForOwner.isEmpty() && <Fragment>
            <label>Organize in Experiences</label>
            <SearchableMultiSelect
              options={experiencesForOwner}
              selectedIds={experienceIds}
              onSelectedIdsChange={this.onExperienceIdsChange}
              newRecordString='Experience'
              showSpinner={!resourceLoaded}
              createNewPending={createNewPending}
              dispatchCreate={(name) => this.createNewExperience(name)} />
          </Fragment>}

          {!recordAsTrip && (eventsForOwner.size > 0 || willHaveEvents) && <Fragment>
            <label>Organize in Events</label>
            <SearchableMultiSelect
              options={eventsForOwner}
              selectedIds={eventIds}
              onSelectedIdsChange={this.onEventIdsChange}
              newRecordString='Event'
              showSpinner={!resourceLoaded}
              createNewPending={createNewPending}
              dispatchCreate={(name) => this.createNewEvent(name)} />
          </Fragment>}

          {!showMoreOptions && <div className={styles.showHideMoreContainer}>
            <a className={styles.showHideMoreCTA} onClick={this.showMoreOptionsClicked}>
              Show More Options
            </a>
          </div>}

          {showMoreOptions && <div ref={e => { this._moreOptions = e }}>
            <MoreOptions
              photoIds={photoIds}
              files={files}
              onPhotosDrop={this.onPhotosDrop}
              onPhotoRemoved={this.onPhotoRemoved}
              onFileRemoved={this.onFileRemoved}
              recreationTypeIds={recreationTypeIds}
              onRecreationTypeIdsChange={this.onRecreationTypeIdsChange}
              allowRecordAsTrip={ownerId === currentUser.id && eventIds.size === 0 && !club}
              recordAsTrip={recordAsTrip}
              onRecordAsTripChange={this.onRecordAsTripChange}
              departedAtDate={departedAtDate}
              onDepartedAtDateChange={this.onDepartedAtDateChange}
              departedAtTime={departedAtTime}
              onDepartedAtTimeChange={this.onDepartedAtTimeChange}
              duration={duration}
              onDurationChange={this.onDurationChange}
              gears={gears}
              gearId={gearId}
              onGearIdChange={this.onGearIdChange}
              showAuditNotes={editId !== 'null'}
              club={club}
              auditNotes={auditNotes}
              onAuditNotesChange={this.onAuditNotesChange}
              availableTags={availableTags}
              selectedTags={selectedTags}
              onSelectedTagsChange={this.onSelectedTagsChange}
              onSelectedIdsChange={this.onSelectedIdsChange}
              locality={locality}
              onLocalityChange={this.onLocalityChange}
              hideMoreOptions={() => this.setState({showMoreOptions: false, recordAsTrip: false})} />
          </div>}
        </form>
      </ModalOverlay>
    )
  }
}

const UpdatePicker = ({editId, routes, mapRouteIds, onChange}) => {
  const {pathname} = window.location
  const availableRoutes = {}

  routes(mapRouteIds).map(r => {
    if (r.permissions.canBeModified) {
      availableRoutes[r.id] = <span>{pathname.includes(`${r.id}`) ? 'Update' : 'Overwrite'} <strong>{r.name}</strong></span>
    }
  })

  const opts = OrderedMap(Object.assign({
    ...availableRoutes
  }, {
    null: <span>Save as new route</span>
  }))

  return (
    <RadioGroup
      options={opts}
      selected={editId}
      onChange={onChange}
    />
  )
}

const Footer = ({onSubmit, savePending, onRequestClose}) => {
  return (
    <div className={styles.footer}>
      <CTA link margins={false} className={styles.specificity} onClick={onRequestClose}>Cancel</CTA>
      <CTA margins={false} disabled={savePending} className={styles.specificity} onClick={onSubmit}>{savePending ? <CircleSpinner size={12} /> : 'Save'}</CTA>
    </div>
  )
}

class MoreOptions extends React.PureComponent {
  componentDidUpdate (prevProps) {
    if (!prevProps.recordAsTrip && this.props.recordAsTrip) {
      this._tripFields.scrollIntoView({behavior: 'smooth'})
    }
  }

  render () {
    const {
      photoIds,
      files,
      onPhotosDrop,
      onPhotoRemoved,
      onFileRemoved,
      recreationTypeIds,
      onRecreationTypeIdsChange,
      recordAsTrip,
      onRecordAsTripChange,
      allowRecordAsTrip,
      departedAtDate,
      onDepartedAtDateChange,
      departedAtTime,
      onDepartedAtTimeChange,
      duration,
      onDurationChange,
      gears,
      gearId,
      onGearIdChange,
      club,
      showAuditNotes,
      auditNotes,
      onAuditNotesChange,
      availableTags,
      selectedTags,
      onSelectedTagsChange,
      locality,
      onLocalityChange,
      hideMoreOptions
    } = this.props

    return (
      <Fragment>
        {club && <ClubFields
          club={club}
          showAuditNotes={showAuditNotes}
          auditNotes={auditNotes}
          onAuditNotesChange={onAuditNotesChange}
          locality={locality}
          availableTags={availableTags}
          selectedTags={selectedTags}
          onSelectedTagsChange={onSelectedTagsChange}
          onLocalityChange={onLocalityChange}
        />}

        <label>Images</label>
        <Images
          photoIds={photoIds}
          files={files}
          onPhotosDrop={onPhotosDrop}
          onPhotoRemoved={onPhotoRemoved}
          onFileRemoved={onFileRemoved} />

        <label>What is this for?</label>
        <CheckboxGroup
          selected={recreationTypeIds}
          options={RECREATION_TYPES}
          onChange={onRecreationTypeIdsChange} />

        {allowRecordAsTrip && <Fragment>
          <label>Record as trip?</label>
          <RadioGroup
            horizontal
            selected={`${recordAsTrip}`}
            options={YES_NO_OPTIONS}
            onChange={onRecordAsTripChange} />
        </Fragment>}

        {recordAsTrip && <div ref={e => { this._tripFields = e }}>
          <label>Started ride on</label>
          <DatePickerInput allowBlank date={departedAtDate} onDateChange={onDepartedAtDateChange} />

          <label>Time</label>
          <TimePickerInput
            showSecond={false}
            defaultValue={departedAtTime}
            onChange={onDepartedAtTimeChange}
            format='h:mm a'
            use12Hours />

          <label>Duration in minutes (optional)</label>
          <input type='number'
            onChange={onDurationChange}
            min={1}
            defaultValue={duration} />

          <label>Gear</label>
          <select className={styles.overwriteLegacySelect} defaultValue={gearId} onChange={onGearIdChange}>
            <option value={null} />
            {gears && gears.valueSeq().map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>}

        <div className={styles.showHideMoreContainer}>
          <a className={styles.showHideMoreCTA} onClick={hideMoreOptions}>
            Show Fewer Options
          </a>
        </div>
      </Fragment>
    )
  }
}

class Images extends React.PureComponent {
  onTriggerFileBrowser = () => {
    this._dropzone && this._dropzone.open()
  }

  render () {
    const {photoIds, files, onPhotosDrop, onPhotoRemoved, onFileRemoved} = this.props
    const noPhotos = photoIds.size === 0 && files.size === 0
    const onePhoto = (photoIds.size + files.size) === 1
    const manyPhotos = (photoIds.size + files.size) > 1

    return (
      <ImageUpload
        ref={r => { this._dropzone = r }}
        multiple
        disableClick
        onDrop={onPhotosDrop}
        className={styles.imgUpload}
        hintTop='Drop photos for your route here'
        hintBottom={<span>You can also use the <a onClick={this.onTriggerFileBrowser}>File Browser</a></span>}>
        {noPhotos && <img className={styles.plusIcon} height='80' width='80' src={plusIcon} />}

        {onePhoto && <div className={styles.imgContainer}>
          {photoIds.map(pid => <Image
            key={pid}
            src={urls.photos.medium(pid)}
            onClick={() => onPhotoRemoved(pid)} />)}
          {files.map((p, i) => <Image
            key={p.size}
            src={p.preview}
            size={p.size}
            onClick={(e) => onFileRemoved(e, i)} />)}
        </div>}

        {manyPhotos && <div className={styles.photoGrid}>
          <div className={styles.imgContainer}>
            {photoIds.map((pid, i) => <Image
              key={i}
              src={urls.photos.medium(pid)}
              onClick={() => onPhotoRemoved(pid)}
              className={styles.multiPhoto} />)}
            {files.toSeq().map((p, i) => <Image
              key={i}
              src={p.preview}
              size={p.size}
              onClick={(e) => onFileRemoved(e, i)}
              className={styles.multiPhoto} />)}
          </div>
        </div>}
      </ImageUpload>
    )
  }
}

const Image = ({src, size, onClick, className}) => {
  return (
    <div className={cx(styles.anchor, className)}>
      <div className={styles.remove}><Icon i='ios-close-circle' size='25px' onClick={onClick} className={styles.removeIcon} /></div>
      <img src={src} size={size} />
    </div>
  )
}

const ClubFields = ({
  club,
  showAuditNotes,
  auditNotes,
  onAuditNotesChange,
  availableTags,
  selectedTags,
  onSelectedTagsChange,
  locality,
  onLocalityChange
}) => {
  const options = [...selectedTags, ...availableTags].map(t => ({value: t, label: t}))

  return <Fragment>
    {showAuditNotes && <Fragment>
      <label>Changelog notes</label>
      <input
        type='text'
        onChange={onAuditNotesChange}
        defaultValue={auditNotes} />
    </Fragment>}

    <label>Tags</label>
    <div className={styles.tagsInput}>
      <Select.Creatable
        multi
        joinValues
        delimiter=','
        value={selectedTags}
        onChange={onSelectedTagsChange}
        options={options} />
    </div>

    <label>Override city location</label>
    <input
      type='text'
      onChange={onLocalityChange}
      defaultValue={locality} />
  </Fragment>
}

// if we're editing a route, we want to fetch its information before we show
// the modal
const loadInitialData = (uid, props) => {
  const {map: {activeRoute}, loadRoute, loadTrip, loadPriviledgedClubs} = props
  const rid = activeRoute.id
  const club = window.rwgps && window.rwgps.config && window.rwgps.config.club
  const troutePromise = () => activeRoute.isTrip ? loadTrip(rid) : loadRoute(rid)
  const eventPromise = () => fetchJSON(`/users/${uid}/owns_events`).then(r => r.event)
  const groupsPromise = () => fetchJSON(`/users/${uid}/admin_groups`).then(r => r.results)
  const clubsPromise = () => loadPriviledgedClubs().then(r => r.value.results)

  return Promise.all([
    rid && troutePromise(),
    uid && eventPromise(),
    uid && groupsPromise(),
    !club && clubsPromise()
  ]).then(([troute, event, groups, privClubs]) => ({troute, event, groups, privClubs}))
}

class LoadableSaveRoute extends React.PureComponent {
  render () {
    const {currentUser, ...props} = this.props
    const id = currentUser && currentUser.id

    return <Loadable
      {...props}
      currentUser={currentUser}
      component={_SaveRoute}
      resourceLoaded={() => loadInitialData(id, props)} />
  }
}

const mapStateToProps = (state, ownProps) => {
  const {map: {activeRoute: activeRouteFromPlanner}} = ownProps

  return {
    gears: ownProps.currentUser.id ? gearSelectors.byUserId(state, ownProps.currentUser.id) : [],
    clubByUserId: (userId) => clubSelectors.byUserId(state, userId),
    experiences: (clubId) => experienceSelectors.byClubId(state, clubId),
    photosByIds: (ids) => photoSelectors.photosForIds(state, ids),
    activeRoute: activeRouteFromPlanner.isTrip
      ? tripSelectors.find(state, activeRouteFromPlanner.id)
      : routeSelectors.find(state, activeRouteFromPlanner.id),
    routes: (ids) => routeSelectors.routesForIds(state, ids)
  }
}

const trouteActions = objectType => objectType === 'trip' ? tripActions : routeActions

const mapDispatchToProps = (dispatch, ownProps) => {
  const {id} = ownProps.currentUser

  return {
    loadGears: () => dispatch(gearActions.list({userId: id})),
    loadEvents: (userId) => fetchJSON(`/users/${userId}/owned_events`),
    loadExperiences: (clubId) => dispatch(experienceActions.loadAllExperiences(clubId)),
    loadPriviledgedClubs: () => dispatch(clubActions.privilegedClubsForUser({userId: id, managesRoutes: true, admin: true})),
    loadRoute: (id) => dispatch(routeActions.read(id, {includeTags: true, allEventIds: true, experienceIds: true})),
    loadTrip: (id) => dispatch(tripActions.read(id)),
    addPhoto: (file, parentType, parentId) => {
      const body = new window.FormData()
      body.append('file', file)
      body.append('parent_type', parentType)
      body.append('parent_id', parentId)

      return dispatch(photoActions.create(body))
        .then(r => dispatch(trouteActions(parentType).photoAdded(parentId, r.action.payload.photo.id)))
    },
    createEvent: (name, userId) => dispatch(eventActions.create({name, userId})),
    createExperience: (name, clubId) => dispatch(experienceActions.create({experience: {club_id: clubId, name}, noRedirect: true})),
    addExperienceRoute: (experienceId, routeId) => dispatch(experienceRoutesActions.addRoute(experienceId, routeId)),
    removeRouteFromExperience: (experienceId, routeId) => dispatch(experienceActions.removeRoute(experienceId, {routeId})),
    createTRoute: (params, objectType) => dispatch(trouteActions(objectType).create(params)),
    updateRoute: (params, _objectType) => {
      if (params.route) {
        const {route, ...rest} = params
        return dispatch(routeActions.update(route, rest))
      } else {
        return dispatch(routeActions.update(params))
      }
    }
  }
}

export const SaveRouteContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableSaveRoute)

const store = initStore()

export const SaveRouteApp = (props) => (
  <Provider store={store}>
    <SaveRouteContainer currentUser={getCurrentUser()} {...props} />
  </Provider>
)
